import { useMemo } from 'react';
import { User } from '../../../api/user';
import { PROFILE_MANDATORY_FIELDS_TO_SUBMIT_KLYDO } from '../../../consts/mandatoryFields';
import useAppContext from '../../../hooks/useAppContext';
import { Collection } from '../../../api/collections';
import {
  MIN_KLYDOS_FOR_COLLECTION,
  MIN_LABELS_FOR_COLLECTION,
  REQUIRED_THUMBNAIL_KLYDOS,
} from '../../../consts/collectionLimitations';
import { PopulatedKlydo } from '../../../api/klydo';
import {
  getCollectionProfile,
  isKlydoCompatibleWithCollection,
} from '../../../utils/collectionValidation';
import {
  getKlydoMissingFields,
  isKlydoDataIncomplete,
} from '../../../utils/klydoValidation';
import { INCONSISTENT_COLLECTION_SOUND_TOOLTIP } from '../../../consts/alerts';

export type CollectionValidationData = Pick<
  Collection,
  'name' | 'description' | 'coverUrl' | 'logoUrl' | 'klydoIds'
> & {
  productPageMetadata?: Pick<
    NonNullable<Collection['productPageMetadata']>,
    | 'logoTypeUrl'
    | 'thumbnailBackgroundUrl'
    | 'badgeColorHex'
    | 'labels'
    | 'thumbnailKlydos'
    | 'mockTemplateId'
  >;
};

export interface CollectionSubmitValidationResult {
  isSubmitDisabled: boolean;
  isProfileMissingFields: boolean;
  handleMissingFields: string;
}

const useCollectionSubmitValidation = (
  collectionData: CollectionValidationData,
  isUpdating = false,
  isUploading = false,
  klydos: PopulatedKlydo[] = [],
): CollectionSubmitValidationResult => {
  const { user, isAdmin } = useAppContext();

  const isProfileMissingFields = useMemo(() => {
    if (isAdmin) return false;
    return PROFILE_MANDATORY_FIELDS_TO_SUBMIT_KLYDO.some(
      (field) => !user?.[field as keyof User],
    );
  }, [user, isAdmin]);

  const hasIncompatibleKlydos = useMemo(() => {
    const profile = getCollectionProfile(klydos);
    if (!profile) return false;
    // A deleted klydo leaves a null entry (see collectionValidation.ts) —
    // skip it instead of letting profileOf(null) throw.
    return klydos.some(
      (k) => k && !isKlydoCompatibleWithCollection(k, profile),
    );
  }, [klydos]);

  const unfinishedKlydos = useMemo(
    () => klydos.filter((k) => k && isKlydoDataIncomplete(k)),
    [klydos],
  );
  const hasUnfinishedKlydos = unfinishedKlydos.length > 0;

  const handleMissingFields = useMemo(() => {
    // Sound consistency comes first: while klydos mix sound profiles, that is
    // the only message — the missing-fields list waits until it is resolved.
    if (hasIncompatibleKlydos) {
      return INCONSISTENT_COLLECTION_SOUND_TOOLTIP;
    }

    const missingFields: string[] = [];

    if (!collectionData.name?.trim()) missingFields.push('Name');
    if (!collectionData.coverUrl) missingFields.push('Cover image');
    if (!collectionData.logoUrl) missingFields.push('Logo');
    if (!collectionData.description) missingFields.push('Description');

    if (
      !collectionData.klydoIds ||
      collectionData.klydoIds.length < MIN_KLYDOS_FOR_COLLECTION
    ) {
      missingFields.push(
        `Klydos (minimum ${MIN_KLYDOS_FOR_COLLECTION} required)`,
      );
    }

    if (unfinishedKlydos.length) {
      const unfinished = unfinishedKlydos
        .map(
          (k) =>
            `${k.name || 'Untitled'}: ${getKlydoMissingFields(k).join(', ')}`,
        )
        .join('; ');
      missingFields.push(`Unfinished klydos (${unfinished})`);
    }

    if (!collectionData.productPageMetadata?.logoTypeUrl) {
      missingFields.push('Logotype');
    }

    if (!collectionData.productPageMetadata?.thumbnailBackgroundUrl) {
      missingFields.push('Thumbnail Background');
    }

    if (!collectionData.productPageMetadata?.badgeColorHex) {
      missingFields.push('Thumbnail Contrast Color');
    }

    if (
      !collectionData.productPageMetadata?.labels ||
      collectionData.productPageMetadata.labels.length <
        MIN_LABELS_FOR_COLLECTION
    ) {
      missingFields.push(
        `Tags (minimum ${MIN_LABELS_FOR_COLLECTION} required)`,
      );
    }

    if (
      !collectionData.productPageMetadata?.thumbnailKlydos ||
      collectionData.productPageMetadata.thumbnailKlydos.length !==
        REQUIRED_THUMBNAIL_KLYDOS
    ) {
      missingFields.push(
        `Thumbnail Klydos (exactly ${REQUIRED_THUMBNAIL_KLYDOS} required)`,
      );
    }

    if (!collectionData.productPageMetadata?.mockTemplateId) {
      missingFields.push('Scene (mock template)');
    }

    return missingFields.length > 0
      ? `Missing fields: ${missingFields.join(', ')}`
      : '';
  }, [
    collectionData.name,
    collectionData.coverUrl,
    collectionData.logoUrl,
    collectionData.description,
    collectionData.klydoIds,
    collectionData.productPageMetadata?.logoTypeUrl,
    collectionData.productPageMetadata?.thumbnailBackgroundUrl,
    collectionData.productPageMetadata?.badgeColorHex,
    collectionData.productPageMetadata?.labels,
    collectionData.productPageMetadata?.thumbnailKlydos,
    collectionData.productPageMetadata?.mockTemplateId,
    hasIncompatibleKlydos,
    unfinishedKlydos,
    isAdmin,
  ]);

  const isSubmitDisabled = useMemo(() => {
    if (isAdmin) return hasIncompatibleKlydos || hasUnfinishedKlydos;

    return (
      !collectionData.coverUrl ||
      !collectionData.name?.trim() ||
      !collectionData.description ||
      !collectionData.logoUrl ||
      !collectionData.productPageMetadata?.logoTypeUrl ||
      !collectionData.productPageMetadata?.thumbnailBackgroundUrl ||
      !collectionData.productPageMetadata?.badgeColorHex ||
      !collectionData.klydoIds ||
      collectionData.klydoIds.length < MIN_KLYDOS_FOR_COLLECTION ||
      !collectionData.productPageMetadata?.labels ||
      collectionData.productPageMetadata.labels.length <
        MIN_LABELS_FOR_COLLECTION ||
      !collectionData.productPageMetadata?.thumbnailKlydos ||
      collectionData.productPageMetadata.thumbnailKlydos.length !==
        REQUIRED_THUMBNAIL_KLYDOS ||
      !collectionData.productPageMetadata?.mockTemplateId ||
      hasIncompatibleKlydos ||
      hasUnfinishedKlydos ||
      isUpdating ||
      isUploading
    );
  }, [
    collectionData.coverUrl,
    collectionData.name,
    collectionData.description,
    collectionData.logoUrl,
    collectionData.productPageMetadata?.logoTypeUrl,
    collectionData.productPageMetadata?.thumbnailBackgroundUrl,
    collectionData.productPageMetadata?.badgeColorHex,
    collectionData.klydoIds,
    collectionData.productPageMetadata?.labels,
    collectionData.productPageMetadata?.thumbnailKlydos,
    collectionData.productPageMetadata?.mockTemplateId,
    hasIncompatibleKlydos,
    hasUnfinishedKlydos,
    isUpdating,
    isUploading,
    isAdmin,
  ]);

  return {
    isSubmitDisabled,
    isProfileMissingFields,
    handleMissingFields,
  };
};

export default useCollectionSubmitValidation;
